import { BookingStepUnavailablePage } from '@components/BookingFlow/Common/BookingStepUnavailablePage';
import { getBookingSummaryTitle } from '@components/BookingFlow/Common/getBookingSummaryTitle';
import { ProposalSpikedTireSeasonBanner } from '@components/BookingFlow/Common/MobileBookingSpikedTireSeasonBanner';
import { NavioContactChatWidget } from '@components/BookingFlow/Common/NavioContactChatWidget';
import { StuddedTireSeasonDialog } from '@components/BookingFlow/Common/StuddedTireSeasonDialog';
import { StationaryInvoicePaymentBanners } from '@components/BookingFlow/Stationary/StationaryInvoicePaymentBanners';
import { StationaryPaymentChoice } from '@components/BookingFlow/Stationary/StationaryPaymentChoice';
import { BookingProposalNotRecallNextSteps } from '@components/BookingProposal/BookingProposalNotRecallNextSteps';
import { ProposalNotConfirmedBanner } from '@components/BookingProposal/ProposalNotConfirmedBanner';
import { ProposalShoppingCart } from '@components/BookingProposal/ProposalShoppingCart';
import { ApiErrorMessageWithTrans } from '@components/Error/ApiErrorMessageWithTrans';
import { ApiErrorPageWithTrans } from '@components/Error/ApiErrorPageWithTrans';
import { ContentWrapperCustomerApp } from '@components/Layouts/ContentWrapperCustomerApp';
import { FlowBackButton } from '@components/Layouts/FlowBackButton';
import { NoddiLoadingScreenWithTrans } from '@components/Loading/NoddiLoadingScreenWithTrans';
import { CommentBox } from '@components/Summary/CommentBox';
import { CommonBookingInfoCard } from '@components/Summary/CommonBookingInfoCard';
import { NotificationSettingsDialog } from '@components/Summary/NotificationSettingsDialog';
import { CustomerDataQualityProvider } from '@components/UserGroupDataQuality/CustomerDataQualityProvider';
import { UserGroupDataQualityPromptCustomer } from '@components/UserGroupDataQuality/UserGroupDataQualityPromptCustomer';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import type { BookingProposalRecord } from '@navio/nsdk';
import { BookingLocationType, PaymentOption, SalesItemBookingCategoryType } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import type { BookingUserNotificationDto } from 'noddi-types';
import { FixedBottomBarContainer, NoddiBasicCard, NoddiDialog } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import { useAuthContext } from 'noddi-web';
import { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useProposalCatalogPrices } from '../../../hooks/useProposalCatalogPrices';
import { useStationaryPaymentChoice } from '../../../hooks/useStationaryPaymentChoice';
import { useTrackProposalStep } from '../../../hooks/useTrackProposalStep';
import {
  useBookingProposalActions,
  useBookingProposalAddress,
  useBookingProposalComment,
  useBookingProposalItems,
  useBookingProposalServiceDepartmentId,
  useBookingProposalTimeWindow,
  useBookingProposalType
} from '../../../stores/bookingProposalStore';
import { isRecallProposalType } from '../../../utils/bookingProposalType';
import { getProposalCartTotal } from '../../../utils/proposalCartTotal';
import { checkoutInvoiceToUserGroupId } from '../../../utils/stationaryCheckout';
import { useCreateBookingFromProposal } from '../hooks/useCreateBookingFromProposal';
import { ConfirmProposalBookingButton } from './ConfirmProposalBookingButton';

export const BookingProposalConfirmation = () => {
  const { slug } = useParams();
  const { isLoggedIn } = useAuthContext();

  const {
    data: proposal,
    isPending: isProposalPending,
    error: proposalError
  } = noddiAsync.useGet({
    type: URLKeys.getBookingProposalBySlug,
    input: { slug: slug! }
  });

  // Login is its own step, so this page is review-and-pay only.
  if (!isLoggedIn) {
    return <Navigate to={customerRoutes.bookingProposalLogin.getPath({ slug: slug! })} replace />;
  }

  if (isProposalPending) {
    return <NoddiLoadingScreenWithTrans />;
  }

  if (proposalError) {
    return <ApiErrorPageWithTrans error={proposalError} />;
  }

  return <ConfirmationContent proposal={proposal} />;
};

const ConfirmationContent = ({ proposal }: { proposal: BookingProposalRecord }) => {
  const { isLoggedIn } = useAuthContext();
  const proposalUserGroupId = proposal.userGroupMember.userGroup.id;
  const [isNotificationDialogOpen, setIsNotificationDialogOpen] = useState(false);
  const address = useBookingProposalAddress();
  const selectedBookingTimeWindow = useBookingProposalTimeWindow();
  const bookingProposalItems = useBookingProposalItems();
  const bookingProposalType = useBookingProposalType();
  const serviceDepartmentId = useBookingProposalServiceDepartmentId();
  const commentsUser = useBookingProposalComment();
  const { setComment } = useBookingProposalActions();
  const { prices: catalogPrices, priceByCartKey } = useProposalCatalogPrices(proposal);
  const navigate = useNavigate();
  const { slug } = useParams();

  // No chosen time renders the restart screen below instead of the summary.
  useTrackProposalStep('summary', !!selectedBookingTimeWindow);

  const {
    data: paymentOptionsData,
    error: paymentOptionsError,
    isLoading: isPaymentOptionsLoading
  } = noddiAsync.useGet({
    type: URLKeys.getPaymentOptions,
    input: {
      userGroupId: proposal.userGroupMember.userGroup.id,
      serviceOrganizationId: proposal.serviceDepartment.serviceOrganizationId,
      serviceDepartmentId: proposal.serviceDepartment.id,
      carIds:
        proposal.bookingProposalItems.length > 0 ? proposal.bookingProposalItems.map((item) => item.car.id) : undefined
    },
    queryConfig: { enabled: isLoggedIn }
  });

  const proposalUserGroup = proposal.userGroupMember.userGroup;
  const { paymentOption, setPaymentOption, isInvoice, showPaymentChoice } = useStationaryPaymentChoice({
    isOrganization: !!proposalUserGroup.isOrganization,
    paymentOptions: paymentOptionsData?.paymentOptions
  });
  const invoiceTo = paymentOptionsData?.invoiceTo ?? null;
  const invoiceRecipientName =
    invoiceTo?.name ||
    proposalUserGroup.name ||
    `${proposal.userGroupMember.user.firstName} ${proposal.userGroupMember.user.lastName}`;
  const invoiceToUserGroupId = checkoutInvoiceToUserGroupId(isInvoice, invoiceTo);

  // Only needed to ask who should be notified, so a failure must not block
  // accepting the proposal -- the backend defaults still notify the driver
  // and the member the booking is for.
  const { data: userGroup, isLoading: isUserGroupLoading } = noddiAsync.useGet({
    type: URLKeys.getUserGroupForCustomer,
    input: { userGroupId: proposalUserGroupId },
    queryConfig: { enabled: isLoggedIn && !!proposalUserGroupId }
  });
  const { data: proposalDrivers } = noddiAsync.useGet({
    type: URLKeys.getBookingProposalDrivers,
    input: { slug: proposal.slug },
    queryConfig: { enabled: isLoggedIn && isNotificationDialogOpen }
  });

  const {
    createProposalBooking,
    isPending,
    hasCreatedBooking,
    studdedTireSeason,
    error: createBookingError
  } = useCreateBookingFromProposal({
    paymentMethod: paymentOption,
    userGroupPayerId: invoiceToUserGroupId,
    onMutate: () => {
      navigate(customerRoutes.bookingProposalBuilding.getPath({ slug: slug! }));
    }
  });

  const totalAmount = getProposalCartTotal(bookingProposalItems, priceByCartKey);
  const shouldPayBefore = paymentOption === PaymentOption.PAY_BEFORE && totalAmount > 0;
  // Saving the last sheet step must not re-enter the gate: the report is still stale.
  const continueAfterDataQuality = () => {
    if (userGroup && userGroup.members.length > 1) {
      setIsNotificationDialogOpen(true);
      return;
    }
    void createProposalBooking();
  };
  // The draft lives in sessionStorage, so a reloaded or restored tab lands here without the chosen time
  // (CUSTOMER-APP-17X). Same screen as the booking flow; a pay-before success clears the draft on this page.
  if (!selectedBookingTimeWindow && !hasCreatedBooking) {
    return (
      <BookingStepUnavailablePage
        missing='timeSlot'
        onStartOver={() => {
          navigate(customerRoutes.bookingProposalLandingPage.getPath({ slug: slug! }));
        }}
      />
    );
  }

  if (isLoggedIn && (isPaymentOptionsLoading || isUserGroupLoading)) {
    return <NoddiLoadingScreenWithTrans />;
  }

  if (paymentOptionsError) {
    return <ApiErrorPageWithTrans error={paymentOptionsError} />;
  }

  // Every member of the recall family counts -- gating on one type left
  // `recall_proposal_tire_hotel` rendering the non-recall next steps.
  const isRecall = !!bookingProposalType && isRecallProposalType(bookingProposalType);
  // Arrival/queue copy and the parking pin are mobile-only: a stationary
  // customer drives the car to the station themselves.
  const isStationary = proposal.bookingLocationType === BookingLocationType.STATIONARY;
  const proposalDriverUserIds = new Set<number>(
    proposalDrivers?.cars.flatMap(({ driver }) => (driver ? [driver.userId] : [])) ??
      (proposal.carManager ? [proposal.carManager.id] : [])
  );

  const hasStoneChipRepair = bookingProposalItems.some((item) =>
    item.salesItemsWithQuantity.some(
      (si) => si.salesItem.bookingCategoryType === SalesItemBookingCategoryType.STONE_CHIP_REPAIR
    )
  );

  return (
    <CustomerDataQualityProvider
      userGroupId={isLoggedIn ? proposalUserGroupId : null}
      bookingLocationType={proposal.bookingLocationType ?? undefined}
    >
      <ContentWrapperCustomerApp title={getBookingSummaryTitle()} className='max-w-screen-md' hideBackButton>
        <div className='mb-24 flex flex-col gap-4 md:mb-0'>
          <ProposalNotConfirmedBanner />
          <ProposalSpikedTireSeasonBanner />
          <UserGroupDataQualityPromptCustomer />
          <CommentBox comment={commentsUser} handleChange={setComment} />

          <NoddiBasicCard>
            <ProposalShoppingCart
              userGroupId={proposal.userGroupMember.userGroup.id}
              wrapCarsInCard={false}
              serviceDepartmentId={serviceDepartmentId}
              bookingProposalItems={bookingProposalItems}
              catalogPrices={catalogPrices}
            />
          </NoddiBasicCard>

          <CommonBookingInfoCard
            isBookingProposal
            address={address}
            timeWindow={selectedBookingTimeWindow}
            excludeSteps={isRecall && !isStationary ? undefined : ['communication']}
            hasStoneChipRepair={hasStoneChipRepair}
            isStationary={isStationary}
          />

          {isLoggedIn && !isRecall && <BookingProposalNotRecallNextSteps isStationary={isStationary} />}

          {showPaymentChoice && (
            <StationaryPaymentChoice
              paymentOption={paymentOption}
              onSelect={setPaymentOption}
              invoiceRecipientName={invoiceRecipientName}
            />
          )}

          {isInvoice && !showPaymentChoice && !!invoiceRecipientName && (
            <StationaryInvoicePaymentBanners
              organizationName={invoiceRecipientName}
              invoiceToName={invoiceTo?.name}
              showInvoiceBanner
              requiresOrganizationNumber={false}
            />
          )}
        </div>
        {studdedTireSeason && <StuddedTireSeasonDialog {...studdedTireSeason} isPending={isPending} />}
        <NoddiDialog
          open={isNotificationDialogOpen}
          onClose={() => setIsNotificationDialogOpen(false)}
          title={t`Who should receive booking updates?`}
          description={t`Choose who should receive SMS updates for this booking.`}
        >
          {/* The dialog stays open when confirmation fails, and the copy in the
            bottom bar below sits behind its overlay. */}
          {createBookingError && <ApiErrorMessageWithTrans error={createBookingError} />}
          <NotificationSettingsDialog
            userGroup={userGroup}
            userGroupId={proposalUserGroupId}
            bookedForUserId={proposal.userGroupMember.user.id}
            driverUserIds={proposalDriverUserIds}
            isConfirming={isPending}
            confirmLabel={shouldPayBefore ? <Trans>Confirm and pay</Trans> : undefined}
            // oxlint-disable-next-line typescript/no-misused-promises -- async onConfirm runs createProposalBooking(); React discards the returned promise, so a rejection is unhandled unless the body catches
            onConfirm={async (notificationsToOverride: BookingUserNotificationDto[]) => {
              await createProposalBooking(notificationsToOverride);
            }}
            licensePlateNumbers={bookingProposalItems.map((item) => item.car.licensePlate.number)}
          />
        </NoddiDialog>

        <FixedBottomBarContainer>
          <div className='flex w-full flex-col gap-3'>
            {createBookingError && !isNotificationDialogOpen && <ApiErrorMessageWithTrans error={createBookingError} />}
            <div className='flex items-center justify-between gap-2'>
              <div className='flex items-center gap-2'>
                <FlowBackButton />
                <NavioContactChatWidget />
              </div>
              <ConfirmProposalBookingButton
                willPay={shouldPayBefore}
                isPending={isPending}
                isLoggedIn={isLoggedIn}
                onContinue={continueAfterDataQuality}
              />
            </div>
          </div>
        </FixedBottomBarContainer>
      </ContentWrapperCustomerApp>
    </CustomerDataQualityProvider>
  );
};
